import {
  ArrowSquareOut,
  ArrowsClockwise,
  BellRinging,
  CalendarPlus,
  Check,
  Copy,
  DeviceMobile,
  Link as LinkIcon,
  MagnifyingGlass,
  Trash,
  WarningCircle,
} from '@phosphor-icons/react';
import {
  suggestIcon,
  type Assignees,
  type BinReminder,
  binLabel,
  binName,
  translateServer,
} from '@shh/shared';
import {
  Avatar,
  Button,
  ChoiceGroup,
  Icon,
  IconButton,
  MultiChoice,
  Sheet,
  Text,
  TextField,
  buttonStyles,
  type Choice,
} from '@shh/ui';
import { useEffect, useState } from 'react';
import {
  calendarFeedLink,
  deleteSource,
  friendlyError,
  previewCalendar,
  saveSource,
  setSourceRemind,
  syncCalendar,
  wasteFind,
  wasteHouseNumbers,
  wasteStreets,
  wasteTypes,
  type WastePlace,
  type WasteStreet,
} from '../../data/api';
import {
  RemindField,
  defaultRemindLabel,
  fromRemindChoice,
  toRemindChoice,
} from '../../components/forms';
import type { M } from '../../data/derive';
import { useFamily } from '../../data/family';
import { iconFor } from '../../lib/icons';
import { dateFormat, locale } from '../../lib/locale';
import { calSettingsText } from '../../i18n/calendarSettings';
import { fill } from '../../i18n/fill';

const timeFmt = dateFormat({ dateStyle: 'short', timeStyle: 'short' });

const person = (m: M): Choice => ({
  value: m.id,
  label: m.name,
  picture: <Avatar name={m.name} color={m.color} emoji={m.emoji} size="sm" decorative />,
});

/** Settings → Kalender: calendar links (school, club, Google, Outlook, iCloud) and the waste calendar. */
export function CalendarSettings() {
  const f = useFamily();
  const [adding, setAdding] = useState<'ics' | 'waste' | null>(null);
  const [syncing, setSyncing] = useState<string>();
  const [reminding, setReminding] = useState<string | null>(null);
  const remindSource = f.sources.find((s) => s.id === reminding);
  return (
    <div className="flex flex-col gap-gap-lg">
      {f.sources.length ? (
        <ul className="flex flex-col gap-gap-xs">
          {f.sources.map((s) => (
            <li
              key={s.id}
              className="flex items-center gap-gap-sm rounded-control bg-surface-raised p-inset-xs"
            >
              <Icon icon={iconFor(s.icon)} size="md" className="text-accent-secondary" />
              <span className="flex flex-1 flex-col">
                <span className="font-semibold">{s.title}</span>
                <span className="text-xs text-text-secondary">
                  {s.kind === 'waste'
                    ? `${s.waste?.street} ${s.waste?.houseNumber ?? ''}`
                    : s.bins
                      ? calSettingsText.wasteLink
                      : calSettingsText.calendarLink}
                  {s.lastSyncAt
                    ? calSettingsText.updated(timeFmt.format(s.lastSyncAt))
                    : calSettingsText.loading}
                </span>
                {s.lastError && (
                  <span className="flex items-center gap-gap-xs text-xs font-semibold text-feedback-danger">
                    <Icon icon={WarningCircle} size="sm" /> {translateServer(s.lastError, locale)}
                  </span>
                )}
              </span>
              <IconButton
                icon={BellRinging}
                aria-label={calSettingsText.remindFor(s.title)}
                onPress={() => setReminding(s.id)}
              />
              <IconButton
                icon={ArrowsClockwise}
                aria-label={calSettingsText.refresh(s.title)}
                isDisabled={syncing === s.id}
                onPress={() => {
                  setSyncing(s.id);
                  void syncCalendar(s.id).finally(() => setSyncing(undefined));
                }}
              />
              <IconButton
                icon={Trash}
                aria-label={calSettingsText.remove(s.title)}
                onPress={() => void deleteSource(f.householdId, s.id)}
              />
            </li>
          ))}
        </ul>
      ) : (
        <Text tone="secondary">{calSettingsText.nothing}</Text>
      )}
      <div className="flex flex-wrap gap-gap-sm">
        <Button icon={LinkIcon} onPress={() => setAdding('ics')}>
          {calSettingsText.connect}
        </Button>
        <Button variant="secondary" icon={CalendarPlus} onPress={() => setAdding('waste')}>
          {calSettingsText.waste}
        </Button>
      </div>
      <Sheet
        isOpen={!!adding}
        onOpenChange={(o) => !o && setAdding(null)}
        title={adding === 'waste' ? calSettingsText.waste : calSettingsText.connect}
        isDismissable={false}
      >
        {adding === 'ics' && <IcsForm onDone={() => setAdding(null)} />}
        {adding === 'waste' && <WasteChooser onDone={() => setAdding(null)} />}
      </Sheet>
      <Sheet
        isOpen={!!remindSource}
        onOpenChange={(o) => !o && setReminding(null)}
        title={calSettingsText.remindTitle(remindSource?.title ?? '')}
      >
        {remindSource && (
          <div className="flex flex-col gap-gap-lg">
            <RemindField
              value={toRemindChoice(remindSource.remind)}
              onChange={(v) =>
                void setSourceRemind(f.householdId, remindSource.id, fromRemindChoice(v))
              }
              defaultLabel={defaultRemindLabel(
                f,
                remindSource.kind === 'waste' || !!remindSource.bins,
              )}
            />
            <Text size="sm" tone="muted">
              {calSettingsText.remindHint}
            </Text>
            <Button className="self-end" onPress={() => setReminding(null)}>
              {calSettingsText.done}
            </Button>
          </div>
        )}
      </Sheet>
      {/* parents' own devices only – never a secret link on the wall */}
      {f.role === 'parent' && <FamilyFeed />}
    </div>
  );
}

type Provider = 'google' | 'outlook' | 'apple' | 'other' | 'waste';

/** Where each provider hides its subscription link – three steps and a direct way there. */
const PROVIDERS: Record<
  Provider,
  {
    label: string;
    name: string;
    open?: { href: string; label: string };
    /** Google shows the secret address only in the browser on a computer, not in its apps. */
    computerOnly?: boolean;
    steps: readonly string[];
  }
> = {
  google: {
    ...calSettingsText.providers.google,
    open: {
      href: 'https://calendar.google.com/calendar/r/settings',
      label: calSettingsText.providers.google.open,
    },
    computerOnly: true,
  },
  outlook: {
    ...calSettingsText.providers.outlook,
    open: {
      href: 'https://outlook.live.com/calendar/0/options/calendar/SharedCalendars',
      label: calSettingsText.providers.outlook.open,
    },
  },
  apple: {
    ...calSettingsText.providers.apple,
    open: { href: 'https://www.icloud.com/calendar/', label: calSettingsText.providers.apple.open },
  },
  other: { ...calSettingsText.providers.other, open: undefined },
  // W2: nearly every waste company offers its dates as a calendar link – any region works
  waste: { ...calSettingsText.providers.waste, open: undefined },
};

/** A search for the local waste calendar – the family's town makes it one tap. */
const wasteSearch = (town?: string) =>
  `https://www.google.com/search?${new URLSearchParams({
    q: `Abfuhrkalender ${town ?? ''} iCal`.replace(/\s+/g, ' '),
  })}`;

const isProviderName = (title: string) => Object.values(PROVIDERS).some((x) => x.name === title);

/** Phones open Google links in the calendar app, which has neither the link nor "abonnieren". */
const onPhone = () => /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
const onAndroid = () => /Android/i.test(navigator.userAgent);

function CopyButton({ text, label }: { text: string; label: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <Button
      variant="secondary"
      icon={copied ? Check : Copy}
      onPress={() => void navigator.clipboard?.writeText(text).then(() => setCopied(true))}
      className="self-start"
    >
      {copied ? calSettingsText.copied : label}
    </Button>
  );
}

const dateFmt = dateFormat({
  weekday: 'short',
  day: 'numeric',
  month: 'numeric',
});

/**
 * Calendar assistant: pick where the calendar lives, follow three steps, paste – and see at
 * once what was found ("23 Termine, z. B. …") before connecting. No more guessing.
 */
function IcsForm({ onDone, waste = false }: { onDone: () => void; waste?: boolean }) {
  const f = useFamily();
  const [provider, setProvider] = useState<Provider | undefined>(waste ? 'waste' : undefined);
  const [title, setTitle] = useState(waste ? PROVIDERS.waste.name : '');
  const [url, setUrl] = useState('');
  const [who, setWho] = useState<string>('all');
  const [busy, setBusy] = useState(false);
  const [checking, setChecking] = useState(false);
  const [result, setResult] = useState<Awaited<ReturnType<typeof previewCalendar>>>();
  const [error, setError] = useState<string>();
  // W2: a link full of bins is asked about – "yes" makes it the waste calendar
  const [asWaste, setAsWaste] = useState<'yes' | 'no'>('yes');
  const [chosen, setChosen] = useState<string[]>([]);
  const [reminder, setReminder] = useState<string>('all');
  const [points, setPoints] = useState('3');
  const looksLikeLink = /^(https?|webcals?):\/\/.+/i.test(url.trim());
  const p = provider ? PROVIDERS[provider] : undefined;
  const bins = result?.bins ?? [];
  const isWaste = !!result && (provider === 'waste' || (!!result.waste && asWaste === 'yes'));

  const check = () => {
    setChecking(true);
    setError(undefined);
    setResult(undefined);
    previewCalendar(url.trim())
      .then((r) => {
        setResult(r);
        setChosen(r.bins ?? []);
        setAsWaste('yes');
        if ((provider === 'waste' || r.waste) && (!title.trim() || isProviderName(title))) {
          setTitle(PROVIDERS.waste.name);
        }
      })
      .catch((e: unknown) => setError(friendlyError(e)))
      .finally(() => setChecking(false));
  };

  return (
    <form className="flex flex-col gap-gap-lg" onSubmit={(e) => e.preventDefault()}>
      <ChoiceGroup<Provider>
        label={calSettingsText.where}
        layout="tile"
        value={provider ?? ('' as Provider)}
        onChange={(v) => {
          setProvider(v);
          if (!title.trim() || isProviderName(title)) setTitle(PROVIDERS[v].name);
        }}
        choices={(Object.keys(PROVIDERS) as Provider[]).map((k) => ({
          value: k,
          label: PROVIDERS[k].label,
        }))}
      />
      {p && (
        <>
          <ol className="flex list-decimal flex-col gap-gap-xs rounded-control bg-surface-sunken p-inset-sm pl-inset-lg text-sm">
            {p.steps.map((s) => (
              <li key={s}>{s}</li>
            ))}
          </ol>
          {p.open && p.computerOnly && onPhone() ? (
            <div className="flex flex-col gap-gap-xs">
              <Text size="sm" weight="semibold">
                {calSettingsText.computerOnly}
              </Text>
              <CopyButton text={p.open.href} label={calSettingsText.copyForComputer} />
            </div>
          ) : (
            p.open && (
              <a
                href={p.open.href}
                target="_blank"
                rel="noreferrer"
                className={buttonStyles({ variant: 'secondary' })}
              >
                <Icon icon={ArrowSquareOut} size="md" /> {p.open.label}
              </a>
            )
          )}
          {provider === 'waste' && (
            <a
              href={wasteSearch(f.household?.location?.name)}
              target="_blank"
              rel="noreferrer"
              className={buttonStyles({ variant: 'secondary' })}
            >
              <Icon icon={ArrowSquareOut} size="md" />{' '}
              {f.household?.location
                ? calSettingsText.searchWasteIn(f.household.location.name)
                : calSettingsText.searchWaste}
            </a>
          )}
          <TextField
            label={calSettingsText.link}
            type="url"
            placeholder={calSettingsText.linkPlaceholder}
            value={url}
            onChange={(v) => {
              setUrl(v);
              setResult(undefined);
              setError(undefined);
            }}
          />
          <Button
            variant="secondary"
            icon={MagnifyingGlass}
            isDisabled={!looksLikeLink || checking}
            onPress={check}
            className="self-start"
          >
            {checking ? calSettingsText.checking : calSettingsText.check}
          </Button>
          {error && (
            <p role="alert" className="font-semibold text-feedback-danger">
              {error}
            </p>
          )}
          {result && (
            <div role="status" className="flex flex-col gap-gap-xs">
              <p className="flex items-center gap-gap-xs font-semibold text-feedback-success">
                <Icon icon={Check} size="md" />
                {calSettingsText.found(result.count)}
              </p>
              {result.examples.length > 0 && (
                <ul className="flex flex-col text-sm text-text-secondary">
                  {result.examples.map((e) => (
                    <li key={`${e.date}${e.start ?? ''}${e.title}`}>
                      {dateFmt.format(new Date(`${e.date}T12:00:00`))}
                      {e.start ? ` ${e.start}` : ''} · {e.title}
                    </li>
                  ))}
                </ul>
              )}
              {result.count === 0 && (
                <Text size="sm" tone="muted">
                  {calSettingsText.noneComing}
                </Text>
              )}
            </div>
          )}
          {result?.waste && provider !== 'waste' && (
            <ChoiceGroup<'yes' | 'no'>
              label={calSettingsText.looksWaste}
              layout="tile"
              value={asWaste}
              onChange={(v) => {
                setAsWaste(v);
                if (v === 'no' && title === PROVIDERS.waste.name) setTitle(p?.name ?? '');
                if (v === 'yes' && (!title.trim() || isProviderName(title))) {
                  setTitle(PROVIDERS.waste.name);
                }
              }}
              choices={[
                { value: 'yes', label: calSettingsText.yesWaste },
                { value: 'no', label: calSettingsText.noWaste },
              ]}
            />
          )}
          {result && (
            <>
              <TextField
                label={calSettingsText.name}
                placeholder={
                  isWaste ? calSettingsText.wasteExample : calSettingsText.calendarExample
                }
                value={title}
                onChange={setTitle}
              />
              {isWaste ? (
                <>
                  {bins.length > 0 && (
                    <MultiChoice
                      label={calSettingsText.whichBins}
                      value={chosen}
                      onChange={setChosen}
                      choices={bins.map((b) => ({ value: b, label: binName(b, locale) }))}
                    />
                  )}
                  <ReminderFields
                    reminder={reminder}
                    onReminder={setReminder}
                    points={points}
                    onPoints={setPoints}
                  />
                </>
              ) : (
                <ChoiceGroup
                  label={calSettingsText.whose}
                  layout="tile"
                  value={who}
                  onChange={setWho}
                  choices={[
                    { value: 'all', label: calSettingsText.everyone },
                    ...f.members.map(person),
                  ]}
                />
              )}
            </>
          )}
          <Text size="sm" tone="muted">
            {calSettingsText.secret}
          </Text>
        </>
      )}
      <div className="grid grid-cols-[auto_1fr] gap-gap-sm">
        <Button variant="secondary" onPress={onDone}>
          {calSettingsText.cancel}
        </Button>
        <Button
          size="lg"
          icon={Check}
          isDisabled={
            !result || !title.trim() || busy || (isWaste && bins.length > 0 && !chosen.length)
          }
          onPress={() => {
            setBusy(true);
            void saveSource(f.householdId, {
              kind: 'ics',
              title: title.trim(),
              icon: isWaste ? 'trash' : suggestIcon(title, 'calendar'),
              url: url.trim(),
              assignees: isWaste ? 'all' : ((who === 'all' ? 'all' : [who]) as Assignees),
              ...(isWaste && {
                bins: {
                  // all ticked = all bins, also ones that only appear later
                  trashTypes: chosen.length === bins.length ? [] : chosen,
                  reminder: toBinReminder(reminder, points),
                },
              }),
              createdBy: f.meId,
            }).then(onDone);
          }}
        >
          {calSettingsText.connectButton}
        </Button>
      </div>
    </form>
  );
}

/**
 * The other direction: the family's own dates on the parents' phones – a subscription link
 * for Google / Apple calendar (read-only there, kept current by the phone).
 */
function FamilyFeed() {
  const f = useFamily();
  const [url, setUrl] = useState<string>();
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [confirmRenew, setConfirmRenew] = useState(false);
  const [error, setError] = useState<string>();
  const load = (renew = false) => {
    setBusy(true);
    setError(undefined);
    calendarFeedLink(renew)
      .then((r) => {
        setUrl(r.url);
        setCopied(false);
        setConfirmRenew(false);
      })
      .catch((e: unknown) => setError(friendlyError(e)))
      .finally(() => setBusy(false));
  };
  const webcal = url?.replace(/^https?:\/\//, 'webcal://');
  return (
    <section
      aria-labelledby="family-feed"
      className="flex flex-col gap-gap-sm rounded-control bg-surface-sunken p-inset-sm"
    >
      <h3 id="family-feed" className="flex items-center gap-gap-xs font-display font-black">
        <Icon icon={DeviceMobile} size="md" /> {calSettingsText.feedTitle}
      </h3>
      <Text size="sm" tone="secondary">
        {calSettingsText.feedText}
      </Text>
      {error && (
        <p role="alert" className="font-semibold text-feedback-danger">
          {error}
        </p>
      )}
      {!url ? (
        <Button icon={LinkIcon} isDisabled={busy} onPress={() => load()} className="self-start">
          {calSettingsText.getLink}
        </Button>
      ) : (
        <div className="flex flex-col gap-gap-sm">
          {onAndroid() && (
            <ol className="flex list-decimal flex-col gap-gap-xs pl-inset-md text-sm">
              <li>{calSettingsText.androidStep1}</li>
              <li>{calSettingsText.androidStep2(f.household?.name ?? calSettingsText.family)}</li>
            </ol>
          )}
          <div className="flex flex-wrap gap-gap-sm">
            {onAndroid() ? (
              <CopyButton
                text={`https://calendar.google.com/calendar/render?cid=${encodeURIComponent(webcal!)}`}
                label={calSettingsText.copyFeed}
              />
            ) : (
              <a
                href={`https://calendar.google.com/calendar/render?cid=${encodeURIComponent(webcal!)}`}
                target="_blank"
                rel="noreferrer"
                className={buttonStyles({ variant: 'primary' })}
              >
                {calSettingsText.subscribeGoogle}
              </a>
            )}
            {!onAndroid() && (
              <a href={webcal} className={buttonStyles({ variant: 'secondary' })}>
                {calSettingsText.subscribeApple}
              </a>
            )}
            <Button
              variant="quiet"
              icon={copied ? Check : Copy}
              onPress={() => void navigator.clipboard?.writeText(url).then(() => setCopied(true))}
            >
              {copied ? calSettingsText.copiedShort : calSettingsText.copyLink}
            </Button>
          </div>
          {confirmRenew ? (
            <div className="flex flex-wrap items-center gap-gap-sm">
              <Text size="sm">{calSettingsText.oldStops}</Text>
              <Button variant="danger" isDisabled={busy} onPress={() => load(true)}>
                {calSettingsText.newLink}
              </Button>
              <Button variant="quiet" onPress={() => setConfirmRenew(false)}>
                {calSettingsText.cancel}
              </Button>
            </div>
          ) : (
            <Button
              variant="quiet"
              icon={ArrowsClockwise}
              onPress={() => setConfirmRenew(true)}
              className="self-start"
            >
              {calSettingsText.renew}
            </Button>
          )}
        </div>
      )}
    </section>
  );
}

/** "Am Vorabend erinnern": who takes the bins out, and for how many stars. */
function ReminderFields(props: {
  reminder: string;
  onReminder: (v: string) => void;
  points: string;
  onPoints: (v: string) => void;
}) {
  const f = useFamily();
  return (
    <>
      <ChoiceGroup
        label={calSettingsText.eveWho}
        layout="tile"
        value={props.reminder}
        onChange={props.onReminder}
        choices={[
          { value: 'none', label: calSettingsText.noReminder },
          { value: 'all', label: calSettingsText.firstComes },
          ...f.members.map(person),
        ]}
      />
      {props.reminder !== 'none' && (
        <ChoiceGroup
          label={calSettingsText.starsPutOut}
          value={props.points}
          onChange={props.onPoints}
          choices={['0', '2', '3', '5'].map((p) => ({
            value: p,
            label: p === '0' ? calSettingsText.none : `+${p}`,
          }))}
        />
      )}
    </>
  );
}

const toBinReminder = (reminder: string, points: string): BinReminder | undefined =>
  reminder === 'none'
    ? undefined
    : { assignees: reminder === 'all' ? 'all' : [reminder], points: Number(points) };

/**
 * "Müllabfuhr" (1.15): the app looks up the family's town and asks its waste company directly –
 * Ort, Straße, Hausnummer, Tonnen, fertig. Towns we do not know (yet) connect their waste
 * company's calendar link instead (W2), which works everywhere.
 */
function WasteChooser({ onDone }: { onDone: () => void }) {
  const f = useFamily();
  const home = f.household?.location?.name ?? '';
  const [query, setQuery] = useState(home);
  const [searched, setSearched] = useState<string | undefined>(home || undefined);
  const [matches, setMatches] = useState<WastePlace[]>();
  const [place, setPlace] = useState<WastePlace>();
  const [byLink, setByLink] = useState(false);
  const [error, setError] = useState<string>();

  const lookup = (town: string) =>
    wasteFind(town)
      .then((m) => {
        setMatches(m);
        setPlace(m.length === 1 ? m[0] : undefined);
      })
      .catch((e: unknown) => setError(friendlyError(e)));
  const search = (town: string) => {
    if (town.trim().length < 2) return;
    setError(undefined);
    setSearched(town.trim());
    setMatches(undefined);
    void lookup(town.trim());
  };
  // the home town is known from "Familie" – look it up right away
  useEffect(() => {
    if (home) void lookup(home);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once, when the sheet opens
  }, []);

  if (byLink) return <IcsForm waste onDone={onDone} />;
  // a town we do not know (yet): straight to the link assistant – it works for every company
  if (matches?.length === 0)
    return (
      <div className="flex flex-col gap-gap-lg">
        <Text tone="secondary">
          {fill(calSettingsText.unknownTown, <strong>{searched}</strong>)}
        </Text>
        <IcsForm waste onDone={onDone} />
        <Button
          variant="quiet"
          className="self-start"
          onPress={() => {
            setMatches(undefined);
            setSearched(undefined);
            setQuery('');
          }}
        >
          {calSettingsText.otherTown}
        </Button>
      </div>
    );
  if (place)
    return (
      <div className="flex flex-col gap-gap-lg">
        <WasteForm place={place} onDone={onDone} />
        <div className="flex flex-col items-start">
          <Button variant="quiet" onPress={() => setPlace(undefined)}>
            {calSettingsText.otherPlace}
          </Button>
          <Button variant="quiet" onPress={() => setByLink(true)}>
            {calSettingsText.otherCompany}
          </Button>
        </div>
      </div>
    );

  return (
    <div className="flex flex-col gap-gap-lg">
      <form
        className="flex items-end gap-gap-sm"
        onSubmit={(e) => {
          e.preventDefault();
          search(query);
        }}
      >
        <TextField
          label={calSettingsText.town}
          value={query}
          onChange={setQuery}
          placeholder={calSettingsText.townExample}
          className="flex-1"
        />
        <Button type="submit" icon={MagnifyingGlass} isDisabled={query.trim().length < 2}>
          {calSettingsText.search}
        </Button>
      </form>
      {searched && !matches && !error && (
        <Text tone="muted" role="status">
          {calSettingsText.searching(searched)}
        </Text>
      )}
      {matches && matches.length > 1 && (
        <ul className="flex flex-col gap-gap-xs" aria-label={calSettingsText.places}>
          {matches.map((m) => (
            <li key={`${m.provider}/${m.service}/${m.placeId}`}>
              <Button
                variant="secondary"
                fullWidth
                className="flex-col items-start py-inset-xs"
                onPress={() => setPlace(m)}
              >
                <span>{m.place}</span>
                <span className="text-sm font-normal text-text-secondary">{m.company}</span>
              </Button>
            </li>
          ))}
        </ul>
      )}
      {!home && !searched && (
        <div className="flex flex-col items-start gap-gap-xs">
          <Text tone="secondary">{calSettingsText.whichTown}</Text>
          <Button variant="quiet" onPress={() => setByLink(true)}>
            {calSettingsText.preferLink}
          </Button>
        </div>
      )}
      {error && (
        <p role="alert" className="font-semibold text-feedback-danger">
          {error}
        </p>
      )}
      <Button variant="secondary" className="self-start" onPress={onDone}>
        {calSettingsText.cancel}
      </Button>
    </div>
  );
}

/** "Restmülltonne 80l-240l" → "Restmülltonne": sizes do not help choosing. */
const binTitle = (t: string) => t.replace(/\s*\d+\s*l\s*-\s*\d+\s*l.*$/i, '').trim();

function WasteForm({ place, onDone }: { place: WastePlace; onDone: () => void }) {
  const f = useFamily();
  const [query, setQuery] = useState('');
  const [streets, setStreets] = useState<WasteStreet[]>([]);
  /** Village on one schedule: no streets, the town is the address. */
  const [townAddress, setTownAddress] = useState<string>();
  const [street, setStreet] = useState<WasteStreet>();
  // undefined = still loading; [] = one schedule for the whole street
  const [numbers, setNumbers] = useState<{ number: string; addressId: string }[]>();
  const [number, setNumber] = useState('');
  // loaded per address – `for` tells whether the list belongs to the current address
  const [types, setTypes] = useState<{ for?: string; list: { name: string; title: string }[] }>({
    list: [],
  });
  const [chosen, setChosen] = useState<string[]>([]);
  const [reminder, setReminder] = useState<string>('all');
  const [points, setPoints] = useState('3');
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const failed = (e: unknown) => setError(friendlyError(e));

  // a village on one schedule has no streets – find out right away, before anyone types
  useEffect(() => {
    void wasteStreets(place, '')
      .then((r) => r.placeAddressId && setTownAddress(r.placeAddressId))
      .catch(failed);
  }, [place]);

  useEffect(() => {
    if (street || query.trim().length < 2) return;
    const t = window.setTimeout(() => {
      void wasteStreets(place, query.trim())
        .then((r) => {
          setStreets(r.streets);
          if (r.placeAddressId) setTownAddress(r.placeAddressId);
        })
        .catch(failed);
    }, 350);
    return () => window.clearTimeout(t);
  }, [query, street, place]);

  useEffect(() => {
    if (!street) return;
    void wasteHouseNumbers(place, street.id).then(setNumbers).catch(failed);
  }, [street, place]);

  const addressId =
    townAddress ??
    (street && numbers
      ? numbers.length
        ? numbers.find((h) => h.number === number)?.addressId
        : street.addressId
      : undefined);

  useEffect(() => {
    if (!addressId) return;
    void wasteTypes(place, addressId)
      .then((t) => {
        // big containers are for businesses – hide them, preselect the usual bins
        const household = t.filter((x) => !/großbehälter|container|gewerbe/i.test(x.title));
        setTypes({ for: addressId, list: household });
        setChosen(
          household
            .filter((x) => /rest|papier|bio|gelb|wertstoff|verpackung/i.test(x.title))
            .map((x) => x.name),
        );
      })
      .catch(failed);
  }, [addressId, place]);

  const typed = number.trim().toLowerCase();
  const all = numbers?.map((h) => h.number) ?? [];
  const suggestions =
    typed && !all.includes(number)
      ? all.filter((n) => n.toLowerCase().startsWith(typed)).slice(0, 8)
      : [];
  const loadingTypes = !!addressId && types.for !== addressId;

  return (
    <form className="flex flex-col gap-gap-lg" onSubmit={(e) => e.preventDefault()}>
      <Text tone="secondary">
        {fill(calSettingsText.collectionFrom, <strong>{place.company}</strong>, place.place)}
      </Text>
      {townAddress ? null : !street ? (
        <>
          <div className="flex items-end gap-gap-sm">
            <Icon icon={MagnifyingGlass} size="lg" className="mb-inset-xs text-text-muted" />
            <TextField
              label={calSettingsText.street}
              placeholder={calSettingsText.streetExample}
              value={query}
              onChange={setQuery}
              autoFocus
              className="flex-1"
            />
          </div>
          <ul className="flex flex-col gap-gap-xs" aria-label={calSettingsText.streets}>
            {streets.map((s) => (
              <li key={s.id}>
                <Button
                  variant="secondary"
                  fullWidth
                  className="justify-start"
                  onPress={() => {
                    setStreet(s);
                    setNumbers(undefined);
                  }}
                >
                  {s.name}
                </Button>
              </li>
            ))}
          </ul>
        </>
      ) : (
        <>
          <div className="flex items-center justify-between gap-gap-sm">
            <Text weight="bold">{street.name}</Text>
            <Button
              variant="quiet"
              onPress={() => {
                setStreet(undefined);
                setNumber('');
                setNumbers(undefined);
                setTypes({ list: [] });
              }}
            >
              {calSettingsText.change}
            </Button>
          </div>
          {numbers && numbers.length > 0 && (
            <div className="flex flex-col gap-gap-sm">
              <TextField
                label={calSettingsText.houseNumber}
                inputMode="text"
                value={number}
                onChange={(v) => setNumber(v.trim())}
                autoFocus
                className="max-w-narrow"
              />
              {suggestions.length > 0 && (
                <div className="flex flex-wrap gap-gap-xs" aria-label={calSettingsText.numbers}>
                  {suggestions.map((n) => (
                    <Button key={n} variant="secondary" onPress={() => setNumber(n)}>
                      {n}
                    </Button>
                  ))}
                </div>
              )}
              {typed && !addressId && !suggestions.length && (
                <Text size="sm" tone="secondary">
                  {calSettingsText.unknownNumber}
                </Text>
              )}
            </div>
          )}
        </>
      )}
      {(loadingTypes || (street && !numbers)) && (
        <Text size="sm" tone="muted" role="status">
          {calSettingsText.findingDates}
        </Text>
      )}
      {!loadingTypes && addressId && types.list.length === 0 && (
        <Text tone="secondary" role="status">
          {calSettingsText.onlyContainers}
        </Text>
      )}
      {!loadingTypes && addressId && types.list.length > 0 && (
        <>
          <MultiChoice
            label={calSettingsText.whichBins}
            value={chosen}
            onChange={setChosen}
            choices={types.list.map((t) => ({
              value: t.name,
              label: locale === 'de' ? binTitle(t.title) : binName(binLabel(t.title), locale),
            }))}
          />
          <ReminderFields
            reminder={reminder}
            onReminder={setReminder}
            points={points}
            onPoints={setPoints}
          />
        </>
      )}
      {error && (
        <p role="alert" className="font-semibold text-feedback-danger">
          {error}
        </p>
      )}
      <div className="grid grid-cols-[auto_1fr] gap-gap-sm">
        <Button variant="secondary" onPress={onDone}>
          {calSettingsText.cancel}
        </Button>
        <Button
          size="lg"
          icon={Check}
          isDisabled={!addressId || loadingTypes || !chosen.length || busy}
          onPress={() => {
            if (!addressId) return;
            setBusy(true);
            void saveSource(f.householdId, {
              kind: 'waste',
              title: calSettingsText.waste,
              icon: 'trash',
              assignees: 'all',
              waste: {
                provider: place.provider,
                service: place.service,
                cityId: place.placeId,
                areaId: addressId,
                city: place.place,
                street: street?.name ?? '',
                houseNumber: number || undefined,
                trashTypes: chosen,
                reminder: toBinReminder(reminder, points),
              },
              createdBy: f.meId,
            })
              .then(onDone)
              .catch((e: unknown) => {
                setBusy(false);
                failed(e);
              });
          }}
        >
          {calSettingsText.getDates}
        </Button>
      </div>
    </form>
  );
}
